import { Badge, InlineError, Skeleton } from '../../components/ui';
import { useCompanySubmissions } from './sampleReview';
import { SUBMISSION_STATUS_LABELS, SUBMISSION_STATUS_TONES } from './types';
import type { SubmissionStatus } from './types';

/**
 * The sample runs tagged for one client, read from the CRM side.
 *
 * This is the return leg of the internal client tag. Sales tags a submission
 * with a company so a reviewer knows who the sample is for; without this view
 * that link only ever pointed one way, and anybody asking "what have we had
 * made for this account?" had to go through the supplier module and remember
 * the tags themselves.
 *
 * Nothing about the supplier appears here on purpose. Who manufactures for
 * which client is not a fact the account view needs, and putting the factory
 * name next to the client name is how the two ends of that wall meet.
 */

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function CompanySampleSubmissions({ companyId }: { companyId: string }) {
  const submissions = useCompanySubmissions(companyId);

  if (submissions.isPending) {
    return <Skeleton lines={2} label="Loading sample submissions" />;
  }
  if (submissions.isError) {
    return <InlineError>Sample submissions could not be loaded.</InlineError>;
  }

  const rows = submissions.data ?? [];
  if (rows.length === 0) {
    return (
      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
        No sample runs tagged for this company yet. Tag one from Supplier Reviews.
      </p>
    );
  }

  return (
    <ul className="company-sample-list">
      {rows.map((submission) => (
        <li key={submission.id} className="company-sample-row">
          <div className="company-sample-head">
            <span className="company-sample-title">{submission.title}</span>
            <Badge tone={SUBMISSION_STATUS_TONES[submission.status as SubmissionStatus]} micro>
              {SUBMISSION_STATUS_LABELS[submission.status as SubmissionStatus]}
            </Badge>
          </div>
          <span className="company-sample-meta">
            sent {shortDate(submission.created_at)}
            {submission.decided_at === null ? '' : ` · decided ${shortDate(submission.decided_at)}`}
          </span>
          {submission.decision_note === '' ? null : (
            <p className="company-sample-note">{submission.decision_note}</p>
          )}
        </li>
      ))}
    </ul>
  );
}
